var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { BillboardFacet, checkVector3Near } from "flavr-common";
import { Vector3 } from "flavr-math";
import { FacetHandler, FacetRemoveHandler, ModelEventHandler, ModelEventType, NewModule } from "@modules/module";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { modelLookAt, modelPool } from "@modules/model";
/**
 * Rotates tagged models towards the local camera so labels, icons or other view-facing content stay readable from the current viewpoint.
 * Three.js Sprites also do this automatically, use `BillboardFacet` if other Modules or Models need to be aware of
 * changes to the model's orientation.
 *
 * Facets:
 * - `BillboardFacet` - Marks models that should face the viewer and provides the billboard configuration.
 *
 * Events:
 * - `ModelEventType.MODEL_DESTROYED` - Stops tracking billboards whose owning model has been removed.
 */
export class BillboardModule extends NewModule {
    /**
     * Initializes the cached camera world position used to detect camera movement between frames.
     */
    constructor() {
        super("BillboardModule");
        this.billboards = new Map();
        this.cameraInfo = {
            cameraWorldPosition: new Vector3().copy(localPlayerContainer.cameraWorldPosition),
            changed: true
        };
    }
    /// Life-cycle events ///
    onPreRender() {
        const newCameraWorldPosition = localPlayerContainer.cameraWorldPosition;
        this.cameraInfo.changed = !checkVector3Near(this.cameraInfo.cameraWorldPosition, newCameraWorldPosition);
        this.cameraInfo.cameraWorldPosition.copy(newCameraWorldPosition);
        Array.from(this.billboards.values()).forEach((billboard) => {
            this.updateBillboardTransform(billboard);
        });
    }
    /// Message handlers ///
    handleModelDestroyedEvent(model, _destroyedPermanently) {
        this.billboards.delete(model.getUID());
    }
    handleBillboardFacet(uid, facet) {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        this.billboards.set(uid, { model, facet, lastWorldPosition: model.getWorldPosition() });
    }
    handleRemoveBillboardFacet(uid, _facet) {
        this.billboards.delete(uid);
    }
    /// Private helper methods ///
    updateBillboardTransform(billboardInfo) {
        const model = billboardInfo.model;
        const cameraWorldPosition = this.cameraInfo.cameraWorldPosition;
        const newModelWorldPosition = model.getWorldPosition();
        const modelPositionChanged = !checkVector3Near(billboardInfo.lastWorldPosition, newModelWorldPosition);
        billboardInfo.lastWorldPosition.copy(newModelWorldPosition);
        if (!(modelPositionChanged || this.cameraInfo.changed))
            return;
        if (checkVector3Near(newModelWorldPosition, cameraWorldPosition))
            return;
        modelLookAt(model, cameraWorldPosition);
    }
}
__decorate([
    ModelEventHandler(ModelEventType.MODEL_DESTROYED)
], BillboardModule.prototype, "handleModelDestroyedEvent", null);
__decorate([
    FacetHandler(BillboardFacet)
], BillboardModule.prototype, "handleBillboardFacet", null);
__decorate([
    FacetRemoveHandler(BillboardFacet)
], BillboardModule.prototype, "handleRemoveBillboardFacet", null);
